import { createContext, useContext, useEffect, useState, useMemo } from "react";
import { introductions, tabsData } from "../assets/data/data";
import { useSelector } from "react-redux";

const QuickLinksContext = createContext();

export const useQuickLinksItems = () => useContext(QuickLinksContext);

export const QuickLinksProvider = ({ children }) => {
  const { iphcPractitioner, userData } = useSelector((state) => state.user);

  const [droppedItems, setDroppedItems] = useState([]);
  const [selectedTab, setSelectedTab] = useState("");
  // Practitioner whose staged changes an admin should review (from a notification
  // click or a ?review= deep link). AdminPractitioners opens the modal + clears it.
  const [reviewPractitionerId, setReviewPractitionerId] = useState(null);
  const [sessionTab, setSessionTab] = useState("");
  const [acimTab, setacimTab] = useState("main");
  const [lessonContent, setLessonContent] = useState(
    introductions[0]?.content || "",
  );

  const hasIphc =
    iphcPractitioner && Object.keys(iphcPractitioner).length > 0;
  const isAdmin = userData?.role === "admin";

  const filteredTabs = useMemo(() => {
    return tabsData.filter((tab) => {
      // IphcPractitioner requirement: tab only shows for active Nirva practitioners
      if (tab.requiresIphc && !hasIphc) {
        return false;
      }
      // Hidden from Nirva practitioners (shown to other roles).
      if (tab.hideForIphc && hasIphc) {
        return false;
      }
      // Admin-only tabs (ADMIN section)
      if (tab.requiresAdmin && !isAdmin) {
        return false;
      }

      return true;
    });
  }, [hasIphc, isAdmin]);

  // Split filtered tabs into primary (top-level), secondary ("Learning & Resources"), and admin.
  const primaryTabs = useMemo(
    () => filteredTabs.filter((tab) => tab.section === "primary"),
    [filteredTabs],
  );
  const secondaryTabs = useMemo(
    () => filteredTabs.filter((tab) => tab.section === "secondary"),
    [filteredTabs],
  );
  const adminTabs = useMemo(
    () => filteredTabs.filter((tab) => tab.section === "admin"),
    [filteredTabs],
  );

  const [tabs, setTabs] = useState(filteredTabs);

  useEffect(() => {
    setTabs(filteredTabs);
  }, [filteredTabs]);

  // Helper: get the set of visible tab IDs based on current filteredTabs
  const visibleTabIds = useMemo(
    () => new Set(filteredTabs.map((t) => t.id)),
    [filteredTabs],
  );

  useEffect(() => {
    // Remove any quick-link shortcuts that reference tabs the user can no longer see
    const hasHiddenItems = droppedItems.some(
      (item) => !visibleTabIds.has(item),
    );
    if (hasHiddenItems) {
      const cleanedItems = droppedItems.filter((item) =>
        visibleTabIds.has(item),
      );
      setDroppedItems(cleanedItems);
      localStorage.setItem("quickLinksItems", JSON.stringify(cleanedItems));
    }
  }, [visibleTabIds, droppedItems]);

  useEffect(() => {
    const savedItems = JSON.parse(localStorage.getItem("quickLinksItems"));
    if (savedItems) {
      // Filter out any persisted items the user can no longer access
      const itemsToLoad = savedItems.filter((item) =>
        visibleTabIds.has(item),
      );
      setDroppedItems(itemsToLoad);
    }
  }, [visibleTabIds]);

  useEffect(() => {
    if (droppedItems.length > 0) {
      localStorage.setItem("quickLinksItems", JSON.stringify(droppedItems));
    }
  }, [droppedItems]);

 useEffect(() => {
   // Check for Stripe onboarding return query params
   const params = new URLSearchParams(window.location.search);

   // Deep link from a "profile update" notification email: open the admin
   // Practitioners tab and flag which practitioner's changes to review.
   const reviewId = params.get("review");
   if (reviewId && isAdmin && visibleTabIds.has("adminPractitioners")) {
     setSelectedTab("adminPractitioners");
     localStorage.setItem("selectedTab", "adminPractitioners");
     setReviewPractitionerId(reviewId);
     // Clean the param so a refresh doesn't reopen it.
     const url = new URL(window.location.href);
     url.searchParams.delete("review");
     window.history.replaceState({}, "", url.toString());
     return;
   }

   // Deep link from a blog notification: open the right blog tab.
   const blogReviewId = params.get("blogReview");
   const blogPostId = params.get("blogPost");
   if (blogReviewId && isAdmin && visibleTabIds.has("blogModeration")) {
     setSelectedTab("blogModeration");
     localStorage.setItem("selectedTab", "blogModeration");
     const url = new URL(window.location.href);
     url.searchParams.delete("blogReview");
     window.history.replaceState({}, "", url.toString());
     return;
   }
   if (blogPostId && visibleTabIds.has("blog")) {
     setSelectedTab("blog");
     localStorage.setItem("selectedTab", "blog");
     const url = new URL(window.location.href);
     url.searchParams.delete("blogPost");
     window.history.replaceState({}, "", url.toString());
     return;
   }
   if (blogReviewId || blogPostId) {
     const url = new URL(window.location.href);
     url.searchParams.delete("blogReview");
     url.searchParams.delete("blogPost");
     window.history.replaceState({}, "", url.toString());
   }

   const stripeOnboarding = params.get("stripe_onboarding");
   const stripeRefresh = params.get("stripe_refresh");

   if (stripeOnboarding === "complete" || stripeRefresh === "true") {
     // Auto-navigate to Nirva Practitioner tab (Payment Setup is now a section within it)
     if (visibleTabIds.has("nirva")) {
       setSelectedTab("nirva");
       localStorage.setItem("selectedTab", "nirva");
     }
     // Note: query params are cleaned up by PaymentSetup component
     return;
   }

   const savedTab = localStorage.getItem("selectedTab");
   if (savedTab) {
     if (savedTab === "paymentSetup") {
       // Payment Setup was moved into Nirva Practitioner section
       const fallback = visibleTabIds.has("nirva") ? "nirva" : "home";
       setSelectedTab(fallback);
       localStorage.setItem("selectedTab", fallback);
     } else if (!visibleTabIds.has(savedTab)) {
       // Saved tab is no longer visible for this user's role -- fall back to home
       setSelectedTab("home");
       localStorage.setItem("selectedTab", "home");
     } else {
       setSelectedTab(savedTab);
     }
   }
 }, [visibleTabIds, isAdmin]);

  useEffect(() => {
    localStorage.setItem("selectedTab", selectedTab);
    if (selectedTab === "") {
      setSelectedTab("home");
    }
  }, [selectedTab]);

  return (
    <QuickLinksContext.Provider
      value={{
        droppedItems,
        setDroppedItems,
        selectedTab,
        setSelectedTab,
        reviewPractitionerId,
        setReviewPractitionerId,
        tabs,
        setTabs,
        primaryTabs,
        secondaryTabs,
        adminTabs,
        sessionTab,
        setSessionTab,
        acimTab,
        setacimTab,
        lessonContent,
        setLessonContent,
      }}
    >
      {children}
    </QuickLinksContext.Provider>
  );
};
